/**
 * Answer checking for fillable forms, shared by the server (authoritative: a form
 * does not count as complete until it passes) and the browser (the same messages
 * shown next to the box as the employee types).
 *
 * Two layers of configuration on a form requirement:
 *  - a `format` on a text field: what a valid entry looks like (a ZIP+4, a month,
 *    a Social Security number, a street address that is not a PO Box, …);
 *  - `rules` across fields: a history table that must cover N years with no gaps,
 *    or blocks that become required because of an earlier answer ("If Yes in 11…").
 */

export const EDP_FIELD_FORMATS = [
  'last_first_middle',
  'street',
  'city_state_zip4',
  'full_address',
  'zip4',
  'email',
  'phone',
  'ssn',
  'date',
  'date_of_birth',
  'month',
  'month_or_present',
] as const;
export type EdpFieldFormat = (typeof EDP_FIELD_FORMATS)[number];

export const EDP_FIELD_FORMAT_LABELS: Record<EdpFieldFormat, string> = {
  last_first_middle: 'Name (Last, First, Middle)',
  street: 'Street address (no PO Box)',
  city_state_zip4: 'City, State and ZIP+4',
  full_address: 'Street, City, State and ZIP+4 (no PO Box)',
  zip4: 'ZIP+4',
  email: 'Email address',
  phone: 'Telephone (10 digits)',
  ssn: 'Social Security Number',
  date: 'Date (MM/DD/YYYY)',
  date_of_birth: 'Date of birth (MM/DD/YYYY)',
  month: 'Month (MM/YYYY)',
  month_or_present: 'Month (MM/YYYY) or "Present"',
};

/** Example placeholder shown in the box for each format. */
export const EDP_FIELD_FORMAT_HINTS: Record<EdpFieldFormat, string> = {
  last_first_middle: 'Smith, John Andrew',
  street: '123 Main St Apt 4B',
  city_state_zip4: 'Tulsa, OK 74103-3821',
  full_address: '123 Main St, Tulsa, OK 74103-3821',
  zip4: '74103-3821',
  email: 'name@example.com',
  phone: '(918) 555-0142',
  ssn: '123-45-6789',
  date: 'MM/DD/YYYY',
  date_of_birth: 'MM/DD/YYYY',
  month: 'MM/YYYY',
  month_or_present: 'MM/YYYY or Present',
};

/** A condition on one answer: a chosen option, or a ticked checkbox (`equals: true`). */
export interface EdpRuleCondition {
  field: string;
  equals: string | boolean;
}

export type EdpFormRule =
  | {
      kind: 'history';
      /** Shown in messages, e.g. "Residence history". */
      label: string;
      /** How far back the table must reach, in years. */
      years: number;
      /** Table rows, most recent first. `fields` are the row's other blocks. */
      rows: { from: string; to: string; fields: string[] }[];
    }
  | {
      kind: 'requires';
      /** Completes "Required because …", e.g. 'you answered "Yes" to 11'. */
      label: string;
      /** Any one matching condition triggers the rule. */
      when: EdpRuleCondition[];
      fields: string[];
      /** Only one of `fields` needs an entry (e.g. tick at least one licence type). */
      atLeastOne?: boolean;
    };

export interface EdpFormIssue {
  /** Field the message belongs to; absent for a message about a whole group. */
  field?: string;
  /** Group heading the message belongs to when it is not about one field. */
  section?: string;
  message: string;
}

/** The subset of a form field the checks need; both the server and browser types satisfy it. */
export interface ValidatableField {
  name: string;
  label: string;
  type: 'text' | 'checkbox' | 'radio' | 'dropdown' | 'choice';
  required: boolean;
  mode?: 'required' | 'optional' | 'staff';
  presetValue?: string;
  options?: string[];
  section?: string;
  format?: EdpFieldFormat;
}

export type FormAnswers = Record<string, string | boolean | undefined>;

function modeOf(field: ValidatableField): 'required' | 'optional' | 'staff' {
  if (field.mode) return field.mode;
  if (field.type === 'checkbox') return field.required ? 'required' : 'optional';
  return 'required';
}

function text(value: string | boolean | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isBlank(field: ValidatableField | undefined, value: string | boolean | undefined) {
  if (field?.type === 'checkbox') return value !== true;
  return text(value) === '';
}

// ---------------------------------------------------------------------------
// Formats
// ---------------------------------------------------------------------------

const MONTH = /^(0[1-9]|1[0-2])\/(\d{4})$/;
const DATE = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(\d{4})$/;
const PO_BOX = /\b(p\.?\s*o\.?\s*box|post\s*office\s*box|pob)\b/i;
const FULL_ADDRESS =
  /^\d[^,]*[A-Za-z]{2,}[^,]*,\s*[A-Za-z][A-Za-z .'-]*,\s*[A-Za-z]{2}\.?,?\s+\d{5}-\d{4}$/;
const CITY_STATE_ZIP4 = /^[A-Za-z][A-Za-z .'-]*,\s*[A-Za-z]{2}\.?,?\s+\d{5}-\d{4}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

/** Months since year 0 for a "MM/YYYY" entry, or undefined when it is not one. */
export function monthIndex(value: string): number | undefined {
  const match = MONTH.exec(value.trim());
  if (!match) return undefined;
  return Number(match[2]) * 12 + Number(match[1]) - 1;
}

export function formatMonthIndex(index: number): string {
  const month = (index % 12) + 1;
  return `${String(month).padStart(2, '0')}/${Math.floor(index / 12)}`;
}

function isPresent(value: string): boolean {
  return /^(present|current|now|to\s*date)$/i.test(value.trim());
}

function parseDate(value: string): Date | undefined {
  const match = DATE.exec(value.trim());
  if (!match) return undefined;
  const [, mm, dd, yyyy] = match;
  const date = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  if (
    date.getUTCFullYear() !== Number(yyyy) ||
    date.getUTCMonth() !== Number(mm) - 1 ||
    date.getUTCDate() !== Number(dd)
  ) {
    return undefined;
  }
  return date;
}

function todayMonthIndex(now: Date): number {
  return now.getUTCFullYear() * 12 + now.getUTCMonth();
}

/** Why `value` is not a valid entry of `format`, or undefined when it is. */
export function formatProblem(
  format: EdpFieldFormat,
  value: string,
  now: Date = new Date(),
): string | undefined {
  const entry = value.trim();
  switch (format) {
    case 'last_first_middle': {
      const parts = entry.split(',').map((part) => part.trim());
      if (parts.length < 2 || parts.slice(0, 2).some((part) => !/^[A-Za-z]/.test(part))) {
        return 'Enter your name as Last, First, Middle (e.g. Smith, John Andrew) — commas included.';
      }
      return undefined;
    }
    case 'street':
      if (PO_BOX.test(entry)) return 'A PO Box is not allowed; enter the street address.';
      if (!/\d/.test(entry) || !/[A-Za-z]{2,}/.test(entry)) {
        return 'Enter the house or building number and street name (e.g. 123 Main St Apt 4B).';
      }
      return undefined;
    case 'city_state_zip4':
      if (CITY_STATE_ZIP4.test(entry)) return undefined;
      if (/\b\d{5}\b/.test(entry) && !/\d{5}-\d{4}/.test(entry)) {
        return 'The form requires the ZIP+4 (e.g. 74103-3821). Look it up at usps.com if you do not know it.';
      }
      return 'Enter as City, ST 12345-6789 (e.g. Tulsa, OK 74103-3821).';
    case 'full_address':
      if (PO_BOX.test(entry)) return 'A PO Box is not allowed; enter the street address.';
      if (FULL_ADDRESS.test(entry)) return undefined;
      if (/\b\d{5}\b/.test(entry) && !/\d{5}-\d{4}/.test(entry)) {
        return 'The form requires the ZIP+4 (e.g. 74103-3821). Look it up at usps.com if you do not know it.';
      }
      return 'Enter the full address as Street, City, ST 12345-6789 (e.g. 123 Main St, Tulsa, OK 74103-3821).';
    case 'zip4':
      if (!/^\d{5}-\d{4}$/.test(entry)) {
        return 'Enter the ZIP+4 as 12345-6789. Look it up at usps.com if you do not know it.';
      }
      return undefined;
    case 'email':
      return EMAIL.test(entry) ? undefined : 'Enter a valid email address (e.g. name@example.com).';
    case 'phone': {
      const digits = entry.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
      return digits.length === 10 ? undefined : 'Enter a 10-digit number with the area code.';
    }
    case 'ssn': {
      const digits = entry.replace(/[-\s]/g, '');
      if (!/^\d{9}$/.test(digits)) return 'Enter the 9-digit Social Security Number (123-45-6789).';
      const area = digits.slice(0, 3);
      if (
        area === '000' ||
        area === '666' ||
        area.startsWith('9') ||
        digits.slice(3, 5) === '00' ||
        digits.slice(5) === '0000'
      ) {
        return 'This is not a valid Social Security Number; check the digits.';
      }
      return undefined;
    }
    case 'date':
    case 'date_of_birth': {
      const date = parseDate(entry);
      if (!date) return 'Enter the date as MM/DD/YYYY (e.g. 04/09/1987).';
      if (date.getTime() > now.getTime()) return 'This date is in the future.';
      if (format === 'date_of_birth') {
        const years = (now.getTime() - date.getTime()) / (365.25 * 24 * 3600 * 1000);
        if (years < 14 || years > 110) return 'Check the year of birth.';
      }
      return undefined;
    }
    case 'month':
    case 'month_or_present': {
      if (format === 'month_or_present' && isPresent(entry)) return undefined;
      const index = monthIndex(entry);
      if (index === undefined) {
        return format === 'month_or_present'
          ? 'Enter the month as MM/YYYY (e.g. 03/2021), or "Present".'
          : 'Enter the month as MM/YYYY (e.g. 03/2021).';
      }
      if (index > todayMonthIndex(now)) return 'This month is in the future.';
      return undefined;
    }
  }
}

// ---------------------------------------------------------------------------
// Whole-form check
// ---------------------------------------------------------------------------

function checkHistory(
  rule: Extract<EdpFormRule, { kind: 'history' }>,
  byName: Map<string, ValidatableField>,
  values: FormAnswers,
  now: Date,
  push: (issue: EdpFormIssue) => void,
): void {
  const rows = rule.rows.map((row) => {
    const names = [row.from, row.to, ...row.fields];
    const used = names.some((name) => !isBlank(byName.get(name), values[name]));
    const section = byName.get(row.from)?.section;
    return { ...row, names, used, section };
  });

  let earliest: number | undefined;
  let previousFrom: number | undefined;
  let lastUsed = -1;
  rows.forEach((row, index) => {
    if (!row.used) return;
    if (index > lastUsed + 1) {
      const skipped = rows[lastUsed + 1];
      if (skipped?.section) {
        push({
          section: skipped.section,
          message: `Fill in the entries in order with no blank row between them (${rule.label}).`,
        });
      }
    }
    lastUsed = index;
    for (const name of row.names) {
      if (isBlank(byName.get(name), values[name])) {
        push({ field: name, message: 'Fill in every block of this entry.' });
      }
    }
    const fromText = text(values[row.from]);
    const toText = text(values[row.to]);
    const from = monthIndex(fromText);
    const to = isPresent(toText) ? todayMonthIndex(now) : monthIndex(toText);
    if (from !== undefined && to !== undefined && from > to) {
      push({ field: row.from, message: '"From" must be the same month as or earlier than "To".' });
    }
    if (index > 0 && isPresent(toText)) {
      push({ field: row.to, message: 'Only the first (current) entry can end "Present".' });
    }
    if (previousFrom !== undefined && to !== undefined && to < previousFrom - 1) {
      push({
        field: row.to,
        message: `Leave no gaps: the entry above starts ${formatMonthIndex(previousFrom)}, so this one must end ${formatMonthIndex(previousFrom)} or ${formatMonthIndex(previousFrom - 1)}.`,
      });
    }
    if (from !== undefined) {
      previousFrom = from;
      earliest = earliest === undefined ? from : Math.min(earliest, from);
    }
  });

  const needBack = todayMonthIndex(now) - rule.years * 12;
  if (lastUsed >= 0 && earliest !== undefined && earliest > needBack) {
    const next = rows[lastUsed + 1];
    if (next?.section) {
      push({
        section: next.section,
        message: `${rule.label} must go back ${rule.years} years, to ${formatMonthIndex(needBack)} or earlier; so far it reaches ${formatMonthIndex(earliest)}. Add the entry before that.`,
      });
    }
  }
}

function conditionMet(condition: EdpRuleCondition, values: FormAnswers): boolean {
  const value = values[condition.field];
  if (typeof condition.equals === 'boolean') return value === condition.equals;
  return typeof value === 'string' && value.trim() === condition.equals;
}

function checkRequires(
  rule: Extract<EdpFormRule, { kind: 'requires' }>,
  byName: Map<string, ValidatableField>,
  values: FormAnswers,
  push: (issue: EdpFormIssue) => void,
): void {
  if (!rule.when.some((condition) => conditionMet(condition, values))) return;
  const blank = rule.fields.filter((name) => isBlank(byName.get(name), values[name]));
  if (rule.atLeastOne) {
    if (blank.length === rule.fields.length) {
      const first = byName.get(rule.fields[0] ?? '');
      push({
        ...(first?.section ? { section: first.section } : { field: rule.fields[0] }),
        message: `Choose at least one because ${rule.label}.`,
      });
    }
    return;
  }
  for (const name of blank) push({ field: name, message: `Required because ${rule.label}.` });
}

/**
 * Every reason the answers are not yet acceptable: blank required fields, entries
 * not in the form's format, and rule breaches. Empty when the form may be submitted.
 * Staff-only fields and fields with a preset value are never the employee's problem.
 */
export function validateFormAnswers(
  fields: readonly ValidatableField[],
  rules: readonly EdpFormRule[],
  values: FormAnswers,
  now: Date = new Date(),
): EdpFormIssue[] {
  const issues: EdpFormIssue[] = [];
  const seen = new Set<string>();
  const push = (issue: EdpFormIssue) => {
    const key = `${issue.field ?? ''}|${issue.section ?? ''}|${issue.message}`;
    if (seen.has(key)) return;
    // One message per field: the first (most fundamental) wins.
    if (issue.field && issues.some((existing) => existing.field === issue.field)) return;
    seen.add(key);
    issues.push(issue);
  };

  const byName = new Map<string, ValidatableField>();
  for (const field of fields) {
    if (modeOf(field) === 'staff') continue;
    byName.set(field.name, field);
    const value = values[field.name];
    if (modeOf(field) === 'required' && !field.presetValue) {
      if (field.type === 'checkbox') {
        if (value !== true) push({ field: field.name, message: 'Required.' });
        continue;
      }
      if (field.type === 'choice' || field.type === 'radio' || field.type === 'dropdown') {
        if (typeof value !== 'string' || !(field.options ?? []).includes(value)) {
          push({ field: field.name, message: 'Choose an answer.' });
        }
        continue;
      }
      if (text(value) === '') {
        push({ field: field.name, message: 'Required.' });
        continue;
      }
    }
    if (field.type === 'text' && field.format && text(value) !== '') {
      const problem = formatProblem(field.format, text(value), now);
      if (problem) push({ field: field.name, message: problem });
    }
  }

  for (const rule of rules) {
    if (rule.kind === 'history') checkHistory(rule, byName, values, now, push);
    else checkRequires(rule, byName, values, push);
  }
  return issues;
}

/** Short labels for a list of issues, e.g. "Still needed: …". */
export function issueLabels(
  fields: readonly ValidatableField[],
  issues: readonly EdpFormIssue[],
): string[] {
  const byName = new Map(fields.map((field) => [field.name, field]));
  const labels: string[] = [];
  for (const issue of issues) {
    const field = issue.field ? byName.get(issue.field) : undefined;
    const label = field ? field.label : (issue.section ?? issue.message);
    if (!labels.includes(label)) labels.push(label);
  }
  return labels;
}
